const HEADER = 'X-CAD-Emergency-Confirmation';
const validToken = value => typeof value === 'string' && /^[a-f0-9]{64}$/i.test(value);

export async function previewEmergencyClearance(api, scope, rangerId, sessionToken, userId) {
  if (!['self', 'ranger', 'all'].includes(scope) || !sessionToken || !Number.isSafeInteger(userId) || userId <= 0)
    throw new Error('Sign in and review the current emergency alerts.');
  const target = scope === 'ranger' && typeof rangerId === 'string' ? rangerId.trim().toUpperCase() : null;
  if (scope === 'ranger' && !target) throw new Error('Select a ranger to review.');
  const response = await api.post('/status/emergency/clearance-preview', { scope, rangerId: target },
    { timeout: 15000, expectedSessionToken: sessionToken });
  const data = response?.data;
  if (response?.status !== 200 || data?.scope !== scope || (scope === 'ranger' && data.rangerId !== target) ||
      !validToken(data.confirmationToken) || !Array.isArray(data.alerts) || data.alerts.some(alert =>
        !alert || !validToken(alert.cycleId) || !Number.isSafeInteger(alert.userId) || alert.userId <= 0 ||
        typeof alert.activatedAtUtc !== 'string' || !Number.isFinite(Date.parse(alert.activatedAtUtc)) ||
        ['rangerId', 'name', 'location'].some(key => alert[key] != null && typeof alert[key] !== 'string') ||
        scope === 'self' && alert.userId !== userId || scope === 'ranger' &&
        (typeof alert.rangerId !== 'string' || alert.rangerId.trim().toUpperCase() !== target)) ||
      new Set(data.alerts.map(alert => alert.cycleId.toUpperCase())).size !== data.alerts.length)
    throw new Error('The current emergency alerts could not be verified. Refresh before confirming.');
  return Object.freeze({ scope, rangerId: target, sessionToken, ownerUserId: userId,
    confirmationToken: data.confirmationToken, alerts: Object.freeze(data.alerts.map(alert => Object.freeze({ ...alert }))) });
}

export async function clearConfirmedEmergency(api, confirmation, { reason, pin, restoreStatus, expectedScope, expectedRangerId } = {}) {
  if (!confirmation || !validToken(confirmation.confirmationToken) || !confirmation.alerts?.length)
    throw new Error('Review the current active emergency alerts before confirming.');
  if (expectedScope && confirmation.scope !== expectedScope || expectedScope === 'ranger' &&
      confirmation.rangerId !== expectedRangerId?.trim().toUpperCase())
    throw new Error('Review the selected emergency alerts before confirming.');
  const route = confirmation.scope === 'self' ? '/status/emergency/self'
    : confirmation.scope === 'all' ? '/status/emergency' : '/status/emergency/' + encodeURIComponent(confirmation.rangerId);
  const prior = typeof restoreStatus === 'string' ? restoreStatus.trim().toLowerCase() : null;
  const restoration = ['available', 'busy', 'out-of-service'].includes(prior) ? prior : undefined;
  const response = await api.delete(route, {
    timeout: 30000, expectedSessionToken: confirmation.sessionToken,
    headers: { [HEADER]: confirmation.confirmationToken },
    ...(confirmation.scope === 'self' ? { data: { reason, pin, restoreStatus: restoration } } : {}),
  });
  const data = response?.data;
  if (response?.status !== 200 || data?.cleared !== confirmation.alerts.length ||
      data?.confirmationToken?.toUpperCase() !== confirmation.confirmationToken.toUpperCase())
    throw new Error('Emergency clearance confirmation was not received. Refresh dispatch before retrying.');
  return data;
}

export async function activateEmergency(api, payload, sessionToken, userId) {
  if (!sessionToken || !Number.isSafeInteger(userId) || userId <= 0) throw new Error('Sign in before activating an emergency.');
  const response = await api.post('/status/emergency', payload, { timeout: 30000, expectedSessionToken: sessionToken });
  const data = response?.data;
  if (response?.status !== 200 || data?.userId !== userId || !validToken(data.cycleId) ||
      !Number.isFinite(Date.parse(data.activatedAtUtc)))
    throw new Error('Emergency confirmation was not received. Check dispatch before retrying.');
  return data;
}
